import config from 'config/appConfig';
import { DivConst } from 'constants/divConst';

import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import mime from 'mime';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { useAtom } from 'jotai';
import type { AcctStateType } from 'stores/atoms/acctState';
import { AcctState } from 'stores/atoms/acctState';
import { getTermSampleWithCompanyName } from '../utils/termSample';
import { submit, uploadS3 } from 'utils/app';
import { openPreviewUrl } from 'utils/terms';

// ------------------------------------------------------------------ //
// 型定義
// ------------------------------------------------------------------ //

export type SettingCompanyFormStateType = {
  name: string;
  tel: string;
  zipCode: string;
  prefectureDiv: number;
  city: string;
  address: string;
  buildingName: string;
  reportMailSenderName: string;
  reportMailSenderAddress: string;
  reportMailReplyAddress: string;
  reportMailSignatureHtml: string;
  privacyPolicyUrl: string;
  copyright?: string;
  termsInputDiv: number;
  termsUrl?: string;
  termsText?: string;
  apiKeyBenchmark?: string;
  apiKeyGoogleAnalytics?: string;
};

export type UseSettingCompanyResult = {
  initialized: boolean;
  processing: boolean;
  processingOnPreview: boolean;
  useFormMethods: UseFormReturn<SettingCompanyFormStateType>;
  imageCropper: {
    isOpen: boolean;
    setIsOpen: (v: boolean) => void;
    isReset: boolean;
    setIsReset: (v: boolean) => void;
    setImageBlob: (b: Blob | null) => void;
  };
  isAdmin: boolean;
  isTermsInputDivUrl: boolean;
  isTermsInputDivText: boolean;
  activePlan: ReturnType<ReturnType<typeof useAppState>['getActivePlan']>;
  acctState: AcctStateType;
  onImageUpload: () => void;
  onImageDelete: () => void;
  onPreviewTerms: () => void;
  onResetTerms: () => void;
  onSave: () => void;
};

// ------------------------------------------------------------------ //
// hook
// ------------------------------------------------------------------ //

/**
 * カンパニー設定画面のロジックを集約するカスタムフック。
 */
export function useSettingCompany(): UseSettingCompanyResult {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();

  const [acctState] = useAtom(AcctState);

  const { updateAcctState, noticeMessageSuccess, noticeMessageFailure, getActivePlan } =
    useAppState();
  const { postCompanyUpdate, postTermsPublish, postTermsPublishTerminate } = useApiState();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [isImageCropperOpen, setIsImageCropperOpen] = useState(false);
  const [isImageCropperReset, setIsImageCropperReset] = useState(false);
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [processingOnPreview, setProcessingOnPreview] = useState(false);

  const useFormMethods = useForm<SettingCompanyFormStateType>({
    defaultValues: {
      termsInputDiv: DivConst.items.termsInputType.text.value,
    },
  });
  const { handleSubmit, setValue, setError, watch } = useFormMethods;

  const termsInputDiv = watch('termsInputDiv');
  const isTermsInputDivUrl =
    termsInputDiv?.toString() === DivConst.items.termsInputType.url.value.toString();
  const isTermsInputDivText =
    termsInputDiv?.toString() === DivConst.items.termsInputType.text.value.toString();

  const activePlan = getActivePlan();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    try {
      _initState(acctState);

      // 利用規約の入力タイプがテキストの場合、テキストが空の場合にサンプルテキストを設定
      // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-formのwatchはjotaiやstateではなくフォーム内部値のみ参照
      const termsText = watch('termsText');
      if (!termsText) {
        setValue('termsText', getTermSampleWithCompanyName(acctState.company?.name));
      }
    } catch (e) {
      showBoundary(e);
    } finally {
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 初回マウント時のみ実行する設計
  }, []);

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 画面のステートを初期化します。
   */
  const _initState = (state: AcctStateType) => {
    setValue('name', state.company?.name ?? '');
    setValue('tel', state.company?.tel ?? '');
    setValue('zipCode', state.company?.zipCode ?? '');
    setValue('prefectureDiv', state.company?.prefectureDiv ?? 0);
    setValue('city', state.company?.city ?? '');
    setValue('address', state.company?.address ?? '');
    setValue('buildingName', state.company?.buildingName ?? '');
    setValue('reportMailSenderName', state.company?.reportMailSenderName ?? '');
    setValue('reportMailSenderAddress', config.ses.from.address);
    setValue('reportMailReplyAddress', state.company?.reportMailReplyAddress ?? '');
    setValue('reportMailSignatureHtml', state.company?.reportMailSignatureHtml ?? '');
    setValue('privacyPolicyUrl', state.company?.privacyPolicyUrl ?? '');
    setValue('copyright', state.company?.copyright ?? undefined);
    setValue(
      'termsInputDiv',
      state.company?.termsInputDiv ?? DivConst.items.termsInputType.text.value
    );
    setValue('termsUrl', state.company?.termsUrl ?? undefined);
    setValue('termsText', state.company?.termsText ?? undefined);
    setValue('apiKeyBenchmark', state.company?.apiKeyBenchmark ?? undefined);
    setValue('apiKeyGoogleAnalytics', state.company?.apiKeyGoogleAnalytics ?? undefined);

    setImageBlob(null);
    setIsImageCropperOpen(false);
    setIsImageCropperReset(false);
  };

  /**
   * 管理権限を持っているかどうかを判定します。
   */
  const _isAdmin = (): boolean => {
    return (
      acctState.user != null &&
      acctState.company?.member?.roleDiv === DivConst.items.role.admin.value
    );
  };

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 画像アップロードボタンクリック時の処理
   */
  const onImageUpload = () => {
    setIsImageCropperOpen(true);
  };

  /**
   * 画像削除ボタンクリック時の処理
   */
  const onImageDelete = () => {
    setIsImageCropperReset(true);
  };

  /**
   * 利用規約プレビューを開く
   */
  const onPreviewTerms = () =>
    submit(processingOnPreview, setProcessingOnPreview, async () => {
      try {
        await postTermsPublish({
          companyId: acctState.company.id,
          termsInputDiv: watch('termsInputDiv'),
          termsText: watch('termsText') ?? '',
          copyright: watch('copyright') ?? '',
          mode: 'preview',
        });
        openPreviewUrl(acctState.company.cd);
      } catch (e) {
        noticeMessageFailure('利用規約のプレビューに失敗しました。');
        showBoundary(e);
      }
    });

  /**
   * 利用規約をリセットする
   */
  const onResetTerms = () => {
    setValue('termsText', getTermSampleWithCompanyName(acctState.company?.name));
  };

  /**
   * 保存ボタン押下時の処理
   */
  const onSave = () =>
    submit(
      processing,
      setProcessing,
      handleSubmit(async (data: SettingCompanyFormStateType) => {
        try {
          const response = await postCompanyUpdate({
            companyId: acctState.company.id,
            name: data.name,
            tel: data.tel,
            zipCode: data.zipCode,
            prefectureDiv: data.prefectureDiv,
            city: data.city,
            address: data.address,
            buildingName: data.buildingName ?? null,
            imageUploadKey: imageBlob
              ? await uploadS3(imageBlob, mime.getExtension(imageBlob.type) ?? 'bin')
              : null,
            imageResetFlag: isImageCropperReset,
            reportMailSenderName: data.reportMailSenderName ?? null,
            reportMailReplyAddress: data.reportMailReplyAddress ?? null,
            reportMailSignatureHtml: data.reportMailSignatureHtml ?? null,
            privacyPolicyUrl: data.privacyPolicyUrl ?? null,
            copyright: data.copyright ?? null,
            termsInputDiv: data.termsInputDiv,
            termsUrl: data.termsUrl ?? null,
            termsText: data.termsText ?? null,
            apiKeyBenchmark: data.apiKeyBenchmark ?? null,
            apiKeyGoogleAnalytics: data.apiKeyGoogleAnalytics ?? null,
          });

          // API のエラー処理
          if (response.error?.duplicated) {
            setError('name', {
              type: 'custom',
              message: '同じ会社は登録できません。',
            });
            return;
          }

          // アカウント関連のステートを更新
          const newState = await updateAcctState(['user', 'company'], acctState.company.cd);

          // 画面のステートを初期化
          _initState(newState as AcctStateType);

          if (isTermsInputDivText) {
            await postTermsPublish({
              companyId: acctState.company.id,
              termsInputDiv: watch('termsInputDiv'),
              termsText: watch('termsText') ?? '',
              copyright: watch('copyright') ?? '',
              mode: 'production',
            });
          } else if (isTermsInputDivUrl) {
            await postTermsPublishTerminate({
              companyId: acctState.company.id,
              termsInputDiv: watch('termsInputDiv'),
              mode: 'production',
            });
          }

          noticeMessageSuccess('カンパニー情報の更新が完了しました。');
        } catch (e) {
          noticeMessageFailure('カンパニー情報の更新に失敗しました。');
          showBoundary(e);
        }
      })
    );

  return {
    initialized,
    processing,
    processingOnPreview,
    useFormMethods,
    imageCropper: {
      isOpen: isImageCropperOpen,
      setIsOpen: setIsImageCropperOpen,
      isReset: isImageCropperReset,
      setIsReset: setIsImageCropperReset,
      setImageBlob,
    },
    isAdmin: _isAdmin(),
    isTermsInputDivUrl,
    isTermsInputDivText,
    activePlan,
    acctState,
    onImageUpload,
    onImageDelete,
    onPreviewTerms,
    onResetTerms,
    onSave,
  };
}
